import React, { useState } from 'react';
import { RiftScar } from '../types/rifts';

interface RiftScarResearchPanelProps {
  scarData: RiftScar;
  onClose: () => void;
  apiBaseUrl: string;
}

export const RiftScarResearchPanel: React.FC<RiftScarResearchPanelProps> = ({
  scarData,
  onClose,
}) => {
  const [error] = useState<string | null>(null);

  const getStabilityColor = (stability: string): string => {
    return stability === 'sealed' ? '#f85149' : '#3fb950';
  };

  const getEffectTypeColor = (effectType: string): string => {
    switch (effectType) {
      case 'vortex': return '#58a6ff';
      case 'tear': return '#bc8cff';
      case 'mist': return '#56d364';
      case 'crystalline': return '#39d0d8';
      default: return '#8b949e';
    }
  };

  const getDangerColor = (level: number): string => {
    if (level <= 3) return '#3fb950';
    if (level <= 6) return '#d29922';
    return '#f85149';
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          border: '2px solid #30363d',
          padding: 0,
          minWidth: '320px',
          maxWidth: '420px',
          fontFamily: '"Sometype Mono", monospace',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <span style={{ color: '#f59e0b', fontSize: '14px', fontWeight: 'bold' }}>
            RIFT SCAR RESEARCH
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #f85149',
              color: '#f85149',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '4px 8px',
              fontFamily: '"Sometype Mono", monospace',
              fontWeight: 'bold',
            }}
          >
            x
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {/* Scar Name */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ color: '#f59e0b', fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
              {scarData.name}
            </div>
            <div style={{ color: '#8b949e', fontSize: '11px' }}>
              {scarData.isDiscovered ? 'Discovered Rift Scar' : 'Undiscovered Rift Scar'}
            </div>
          </div>

          {/* Status Info */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div style={{ border: '1px solid #30363d', padding: '8px', backgroundColor: '#161b22' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>STABILITY</div>
                <div style={{ color: getStabilityColor(scarData.stability), fontSize: '12px', fontWeight: 'bold' }}>
                  {scarData.stability.toUpperCase()}
                </div>
              </div>
              <div style={{ border: '1px solid #30363d', padding: '8px', backgroundColor: '#161b22' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>EFFECT</div>
                <div style={{ color: getEffectTypeColor(scarData.effectType), fontSize: '12px', fontWeight: 'bold' }}>
                  {scarData.effectType.toUpperCase()}
                </div>
              </div>
              <div style={{ border: '1px solid #30363d', padding: '8px', backgroundColor: '#161b22' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>DANGER</div>
                <div style={{ color: getDangerColor(scarData.dangerLevel), fontSize: '12px', fontWeight: 'bold' }}>
                  {scarData.dangerLevel}/10
                </div>
              </div>
              <div style={{ border: '1px solid #30363d', padding: '8px', backgroundColor: '#161b22' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>STATUS</div>
                <div style={{ color: scarData.isActive ? '#3fb950' : '#f85149', fontSize: '12px', fontWeight: 'bold' }}>
                  {scarData.isActive ? 'ACTIVE' : 'INACTIVE'}
                </div>
              </div>
            </div>
          </div>

          {/* Connection Info */}
          <div style={{ marginBottom: '16px', border: '1px solid #30363d', padding: '12px', backgroundColor: '#161b22' }}>
            <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px', fontWeight: 'bold' }}>CONNECTION</div>
            <div style={{ fontSize: '11px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#8b949e' }}>Source:</span>
                <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>{scarData.sourceRift}</span>
              </div>
              <div style={{ textAlign: 'center', color: '#484f58', margin: '4px 0' }}>↓</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#8b949e' }}>Destination:</span>
                <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>{scarData.destinationRift}</span>
              </div>
            </div>
          </div>

          {/* Discovery Info */}
          {scarData.isDiscovered && scarData.discoveredBy && (
            <div style={{ marginBottom: '16px', border: '1px solid #1f6feb', padding: '12px', backgroundColor: '#0d1117' }}>
              <div style={{ color: '#58a6ff', fontSize: '10px', marginBottom: '8px', fontWeight: 'bold' }}>DISCOVERY</div>
              <div style={{ fontSize: '11px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: '#8b949e' }}>Discovered By:</span>
                  <span style={{ color: '#58a6ff' }}>{scarData.discoveredBy}</span>
                </div>
                {scarData.discoveredAt && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8b949e' }}>Discovered At:</span>
                    <span style={{ color: '#58a6ff' }}>
                      {new Date(scarData.discoveredAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Coordinates */}
          <div style={{ marginBottom: '16px', border: '1px solid #30363d', padding: '12px', backgroundColor: '#161b22' }}>
            <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px', fontWeight: 'bold' }}>LOCATION</div>
            <div style={{ fontSize: '11px' }}>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ color: '#8b949e', marginBottom: '2px' }}>World Position:</div>
                <div style={{ color: '#f59e0b', fontFamily: 'monospace' }}>
                  ({Math.floor(scarData.worldX)}, {Math.floor(scarData.worldY)})
                </div>
              </div>
              <div>
                <div style={{ color: '#8b949e', marginBottom: '2px' }}>Spawn Point:</div>
                <div style={{ color: '#f59e0b', fontFamily: 'monospace' }}>
                  ({Math.floor(scarData.destSpawnWorldX)}, {Math.floor(scarData.destSpawnWorldY)})
                </div>
              </div>
            </div>
          </div>

          {/* Warning for Sealed Scars */}
          {scarData.stability === 'sealed' && (
            <div style={{ border: '1px solid #da3633', padding: '12px', backgroundColor: '#1a1313' }}>
              <div style={{ color: '#f85149', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>⚠ SEALED</div>
              <div style={{ color: '#f85149', fontSize: '10px' }}>
                Cannot be traversed. Connection blocked.
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div style={{ border: '1px solid #da3633', padding: '12px', backgroundColor: '#1a1313', marginTop: '16px' }}>
              <div style={{ color: '#f85149', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>Error</div>
              <div style={{ color: '#f85149', fontSize: '10px' }}>{error}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
